import {Button, ButtonSpinner, Form, Input, Locale, Select, Slideout, TextArea} from '@local/ui/components'

import CloseSVG from '@local/assets/svgs/icon-close.svg'
import {useEffect, useState} from 'react'
import {EMAIL_PATTERN, emailIsValid} from '@local/utils/string'
import {useTranslate} from '@local/hooks'

interface AskQuestionSlideoutProps {
	listID: string
	successText: string
	successTitle: string
	retreatList: Array<{label:string, id:string, value:string}>
	isOpen: boolean
	onClickClose: () => void
}

export const AskQuestionSlideout = ({isOpen = false, onClickClose, listID, retreatList, successText, successTitle}:AskQuestionSlideoutProps) => {
	const locale = useTranslate()

	const [email, setEmail] = useState<string>('')
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [isSuccess, setIsSuccess] = useState<boolean>(false)
	const [hasError, setHasError] = useState<boolean>(false)

	const handleSubmit = async ({body}) => {
		setIsLoading(true)
		try {
			setIsSuccess(false)
			setHasError(false)

			if (!emailIsValid(body.email)) {
				throw new Error('InvalidEmail')
			}

			const res = await fetch('/api/contact', {
				method: 'POST',
				body: JSON.stringify({
					listID,
					name: body.name,
					message: body.text,
					retreat: body.retreat,
					emailAddress: body.email,
				}),
			})
			const result = await res.json()

			if (res.status === 200) {
				setIsSuccess(true)
				setIsLoading(false)
				return {status: 200}
			} else {
				setIsLoading(false)
				setHasError(true)
				return {errors: [result]}
			}
		} catch (error) {
			setIsLoading(false)
			setHasError(true)

			if (error.message === 'InvalidEmail') {
				return {errors: [{code: 400, message: 'Invalid email'}]}
			}
			if (error.message === 'RequestFailed') {
				return {errors: [{code: 500, message: 'Request failed'}]}
			}
		}
	}

	useEffect(() => {
		if (isSuccess) {
			setTimeout(() => {
				onClickClose()
				setIsSuccess(false)
			}, 2000)
		}
	}, [isSuccess])

	return (
		<Slideout
			isOpen={isOpen}
			variant='right'
			onClickClose={onClickClose}
			className='bg-grey-4 text-white relative !px-0'>
			<div data-lenis-prevent className='overflow-y-scroll min-h-screen h-fit margin-px-1 flex flex-col scrollbar-hide'>
				<div className='sticky top-0 z-1 flex items-center justify-between w-full py-10 lg:py-20 bg-grey-4'>
					<h2 className='w-fit uppercase'>{locale('Have a question?')}</h2>
					<Button
						small
						variant='icon'
						onClick={onClickClose}
						className='px-16 aspect-square'>
						<CloseSVG />
					</Button>
				</div>
				<Form
					onSubmit={handleSubmit}
					successMessage={<SuccessPanel title={successTitle} text={successText} />}
					errorClassName='bottom-8 text-center'
					successClassName='z-5 mt-0 -inset-1 w-[calc(100%+2px)]'
					className='flex flex-col h-full w-full pt-40 gap-14'>

					<Input
						required
						variant='dark'
						type='text'
						name='name'
						label={locale('Name')}
						className='h-48'
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='bg-grey-4' />
					<Input
						variant='dark'
						type='email'
						name='email'
						label='Email'
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						onInvalid={() => setHasError(true)}
						pattern={EMAIL_PATTERN}
						hasError={hasError}
						className='h-48'
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='bg-grey-4' />

					<Select
						required
						theme='dark-grey'
						name='retreat'
						options={retreatList.filter(Boolean)}
						placeholder={locale('Retreat you are interested in')} />

					<TextArea
						required
						variant='dark'
						name='text'
						label={locale('What are you interested in?')}
						className='h-230'
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='bg-grey-4' />

					<ButtonSpinner
						type='submit'
						small
						loading={isLoading}
						variant='primary-transparent'
						className='mt-auto mb-24'>
						<Locale>Send</Locale>
					</ButtonSpinner>
				</Form>
			</div>
		</Slideout>
	)
}

const SuccessPanel = ({title, text}) => (
	<div className='flex flex-col items-start pt-40 w-full h-full gap-14 bg-grey-4 normal-case text-left'>
		<h3 className='text-32 font-serif tracking-tight'>{title}</h3>
		<p className='text-12 text-grey-1 lg:span-mr-1-wide'>{text}</p>
	</div>
)
